import React, { useCallback, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { countUnsentRounds } from '../services/fieldScoutSyncService';
import SignOutConfirm from '../components/shell/SignOutConfirm';
import ErrorBoundary from '../components/ErrorBoundary';

/**
 * Sign-out that warns first when scouting rounds are still waiting to upload.
 * No unsent rounds (or the count cannot be read) → signs out straight away.
 * A second request while one is running is ignored. A sign-out that fails
 * (for example offline) is logged, never thrown.
 * Render `confirm` somewhere in the component using this hook.
 */
export function useGuardedLogout(): { requestLogout: () => Promise<void>; confirm: React.ReactNode } {
  const { logout } = useAuth();
  const [unsent, setUnsent] = useState(0);
  const busy = useRef(false);

  const safeLogout = useCallback(async () => {
    try {
      await logout();
    } catch (err) {
      console.warn('[SignOut] Sign-out request failed', err);
    }
  }, [logout]);

  const requestLogout = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      let count = 0;
      try {
        count = await countUnsentRounds();
      } catch {
        count = 0;
      }
      if (count > 0) {
        setUnsent(count);
        return;
      }
      await safeLogout();
    } finally {
      busy.current = false;
    }
  }, [safeLogout]);

  const close = useCallback(() => setUnsent(0), []);
  const proceed = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setUnsent(0);
    try {
      await safeLogout();
    } finally {
      busy.current = false;
    }
  }, [safeLogout]);

  const confirm = unsent > 0
    ? React.createElement(
        ErrorBoundary,
        {
          moduleName: 'Sign out',
          children: React.createElement(SignOutConfirm, { count: unsent, onConfirm: proceed, onCancel: close }),
        },
      )
    : null;

  return { requestLogout, confirm };
}
